/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import DashboardShell from "@/components/dashboard/DashboardShell";
import {
  ArrowLeft, CheckCircle2, AlertTriangle, Sparkles, Lightbulb,
  BookOpen, Heart, Share2, ExternalLink, PlayCircle,
} from "lucide-react";
import { useLang } from "@/lib/i18n";
import { getCategoryMeta, getLevelMeta, DEFAULT_BP_ICON } from "@/lib/bestPracticesMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LINKED_MODULE_MAP = {
  animals: { label_fr: "Ouvrir le registre des animaux", label_ar: "افتح سجل الحيوانات", to: "/dashboard/client/animals" },
  health: { label_fr: "Ouvrir le carnet sanitaire", label_ar: "افتح السجل الصحي", to: "/dashboard/client/health" },
  reproduction: { label_fr: "Ouvrir la reproduction", label_ar: "افتح التكاثر", to: "/dashboard/client/repro" },
  nutrition: { label_fr: "Ouvrir le calculateur de ration", label_ar: "افتح حاسبة العلف", to: "/dashboard/client/nutrition" },
  calendar: { label_fr: "Ouvrir le calendrier", label_ar: "افتح التقويم", to: "/dashboard/client/calendar" },
  quick_entry: { label_fr: "Saisie rapide", label_ar: "إدخال سريع", to: "/dashboard/client/quick-entry" },
  famacha: { label_fr: "Ouvrir le suivi FAMACHA", label_ar: "افتح متابعة فاماشا", to: "/dashboard/client/animals" },
};

const isArabicLang = (lang) => lang === "ar" || lang === "tn";
const pick = (item, field, lang) => {
  if (isArabicLang(lang) && item[`${field}_ar`]) return item[`${field}_ar`];
  return item[field];
};

export default function BestPracticeDetail() {
  const { bpId } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const [item, setItem] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [favBusy, setFavBusy] = useState(false);

  useEffect(() => {
    setBusy(true);
    axios.get(`${API}/best-practices/${bpId}`, { withCredentials: true })
      .then((res) => setItem(res.data))
      .catch((err) => setError(err?.response?.data?.detail || "Fiche introuvable"))
      .finally(() => setBusy(false));
  }, [bpId]);

  const toggleFavorite = async () => {
    if (!item || favBusy) return;
    setFavBusy(true);
    const next = !item.is_favorite;
    setItem({ ...item, is_favorite: next });
    try {
      if (next) await axios.post(`${API}/best-practices/${item.id}/favorite`, {}, { withCredentials: true });
      else await axios.delete(`${API}/best-practices/${item.id}/favorite`, { withCredentials: true });
      toast.success(next ? "Ajouté aux favoris ❤️" : "Retiré des favoris");
    } catch {
      setItem({ ...item, is_favorite: !next });
      toast.error("Erreur de mise à jour");
    } finally {
      setFavBusy(false);
    }
  };

  const share = async () => {
    if (!item) return;
    const url = window.location.href;
    const text = pick(item, "titre", lang);
    if (navigator.share) {
      try { await navigator.share({ title: text, text: pick(item, "resume", lang), url }); return; } catch { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié dans le presse-papier 📋");
    } catch {
      toast.error("Impossible de copier");
    }
  };

  return (
    <DashboardShell allowedRoles={["client", "owner", "trainer"]}>
      <button
        type="button"
        onClick={() => navigate(-1)}
        data-testid="bp-back-btn"
        className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#5C6B5C] hover:text-[#1C3A27]"
      >
        <ArrowLeft size={16} /> Retour
      </button>

      {busy && <p className="text-[#5C6B5C] py-10 text-center">Chargement…</p>}

      {!busy && error && (
        <div className="rounded-2xl bg-white border border-[#E2E4DF] p-8 text-center">
          <DEFAULT_BP_ICON size={36} className="mx-auto text-[#A8AFA1] mb-3" />
          <p className="font-bold text-[#1C3A27]">{error}</p>
          <Link to="/dashboard/best-practices" className="inline-block mt-3 text-[#E05A3D] underline text-sm">Voir toutes les fiches</Link>
        </div>
      )}

      {!busy && item && (
        <FicheContent
          item={item}
          lang={lang}
          onToggleFav={toggleFavorite}
          onShare={share}
        />
      )}
    </DashboardShell>
  );
}

function FicheContent({ item, lang, onToggleFav, onShare }) {
  const cat = getCategoryMeta(item.categorie);
  const lvl = getLevelMeta(item.niveau);
  const CatIcon = cat.icon;
  const titre = pick(item, "titre", lang);
  const resume = pick(item, "resume", lang);
  const contenu = pick(item, "contenu", lang);
  const aRetenir = pick(item, "a_retenir", lang);
  const erreurs = pick(item, "erreurs_a_eviter", lang);
  const conseils = pick(item, "conseils_pratiques", lang);
  const bonASavoir = pick(item, "bon_a_savoir", lang);
  const linked = item.linked_module ? LINKED_MODULE_MAP[item.linked_module] : null;

  return (
    <article className="max-w-4xl">
      {/* Hero */}
      <div className="rounded-3xl overflow-hidden bg-white border border-[#E2E4DF] shadow-sm">
        <div
          className="aspect-[21/9] bg-gradient-to-br from-[#F0F0EB] to-[#E2E4DF] relative"
          style={item.image ? { backgroundImage: `url(${item.image})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
        >
          {!item.image && (
            <div className="absolute inset-0 grid place-items-center">
              <CatIcon size={80} style={{ color: cat.color, opacity: 0.4 }} />
            </div>
          )}
          <div className="absolute top-4 start-4 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider text-white shadow-md" style={{ backgroundColor: cat.color }}>
              <CatIcon size={12} /> {cat.label}
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider text-white shadow-md" style={{ backgroundColor: lvl.color }}>
              {lvl.label}
            </span>
          </div>
          {/* Actions bar */}
          <div className="absolute top-4 end-4 flex gap-2">
            <button
              type="button"
              onClick={onToggleFav}
              data-testid="bp-fav-btn"
              className={`w-10 h-10 rounded-full grid place-items-center shadow-md transition-all ${
                item.is_favorite ? "bg-[#E05A3D] text-white" : "bg-white text-[#1C3A27] hover:scale-110"
              }`}
              title={item.is_favorite ? "Retirer des favoris" : "Ajouter aux favoris"}
            >
              <Heart size={18} fill={item.is_favorite ? "white" : "transparent"} strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={onShare}
              data-testid="bp-share-btn"
              className="w-10 h-10 rounded-full bg-white text-[#1C3A27] grid place-items-center shadow-md hover:scale-110 transition"
              title="Partager"
            >
              <Share2 size={18} />
            </button>
          </div>
        </div>
        <div className="p-6 md:p-8">
          <h1 className="font-display text-3xl md:text-4xl font-black text-[#1C3A27] leading-tight">{titre}</h1>
          {resume && <p className="mt-3 text-lg text-[#5C6B5C] leading-relaxed">{resume}</p>}
          {(item.mots_cles || []).length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5">
              {item.mots_cles.map((kw) => (
                <span key={kw} className="px-2.5 py-1 rounded-full bg-[#F0F0EB] text-[#5C6B5C] text-xs font-semibold">#{kw}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Linked module CTA */}
      {linked && (
        <Link
          to={linked.to}
          data-testid="bp-linked-module"
          className="mt-6 group flex items-center justify-between gap-4 p-5 rounded-2xl bg-gradient-to-r from-[#1C3A27] to-[#142A1C] text-white hover:from-[#142A1C] hover:to-[#0E1F14] transition-all"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-[#E05A3D]/30 grid place-items-center flex-shrink-0">
              <CatIcon size={22} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-white/70 font-bold">Action directe dans Mazra3ti</p>
              <p className="font-display text-lg font-bold">{lang === "ar" || lang === "tn" ? linked.label_ar : linked.label_fr}</p>
            </div>
          </div>
          <ExternalLink size={20} className="flex-shrink-0 group-hover:translate-x-1 transition" />
        </Link>
      )}

      {/* Contenu */}
      {contenu && (
        <section data-testid="bp-content" className="mt-8 rounded-2xl bg-white border border-[#E2E4DF] p-6 md:p-8">
          <div className="prose prose-sm md:prose-base max-w-none text-[#1C3A27] whitespace-pre-wrap leading-relaxed">{contenu}</div>
        </section>
      )}

      {/* Video embed */}
      {item.video_url && (
        <section className="mt-6 rounded-2xl overflow-hidden bg-black aspect-video border border-[#E2E4DF]">
          <iframe
            src={item.video_url}
            title="Vidéo explicative"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </section>
      )}

      {/* Inline explanatory images */}
      {(item.images_inline || []).length > 0 && (
        <section className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {item.images_inline.map((img, i) => (
            <figure key={i} className="rounded-2xl overflow-hidden bg-white border border-[#E2E4DF]">
              <img src={img.url} alt={img.caption || ""} className="w-full aspect-video object-cover" loading="lazy" />
              {(img.caption || img.caption_ar) && (
                <figcaption className="px-4 py-2 text-xs text-[#5C6B5C] italic">
                  {isArabicLang(lang) && img.caption_ar ? img.caption_ar : img.caption}
                </figcaption>
              )}
            </figure>
          ))}
        </section>
      )}

      {/* Highlight boxes — 4 boxes */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {aRetenir && <HighlightBox color="#1C7A3C" bg="#E8F2EC" icon={CheckCircle2} title="À retenir" text={aRetenir} testId="bp-retenir" />}
        {erreurs && <HighlightBox color="#C84B31" bg="#FBEDE9" icon={AlertTriangle} title="Danger / Erreurs à éviter" text={erreurs} testId="bp-erreurs" />}
        {conseils && <HighlightBox color="#D97706" bg="#FDF2E0" icon={Sparkles} title="Conseils pratiques" text={conseils} testId="bp-conseils" />}
        {bonASavoir && <HighlightBox color="#1E6BBA" bg="#E3EEFA" icon={Lightbulb} title="Bon à savoir" text={bonASavoir} testId="bp-bonasavoir" />}
      </div>

      <div className="mt-10 text-center">
        <Link to="/dashboard/best-practices" className="inline-flex items-center gap-2 text-sm font-semibold text-[#E05A3D] hover:underline">
          <BookOpen size={14} /> Voir toutes les fiches
        </Link>
      </div>
    </article>
  );
}

function HighlightBox({ color, bg, icon: Icon, title, text, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl p-5 border-s-4" style={{ backgroundColor: bg, borderColor: color }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={18} style={{ color }} />
        <h3 className="font-display text-sm font-bold uppercase tracking-wider" style={{ color }}>{title}</h3>
      </div>
      <p className="text-sm text-[#1C3A27] whitespace-pre-wrap leading-relaxed">{text}</p>
    </div>
  );
}
